import * as THREE from "three"
import Experience from "../Experience.js"

/** Cinematic racing environment: warm late-afternoon light, atmospheric horizon and a natural sky. */
export default class Environment {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.debug = this.experience.debug
        this.scene.background = new THREE.Color(0x8b9eae)
        this.scene.fog = new THREE.FogExp2(0x9ba6ad, 0.0018)
        this.colors = { cloud: 0xc9d2dc }
        if (this.debug.active) this.debugFolder = this.debug.gui.addFolder('cinematic environment')
        this.setSunLight()
        this.setSky()
        this.addSunGlow()
        this.addDistantLandscape()
        this.addClouds()
    }

    setSunLight() {
        this.sunLight = new THREE.DirectionalLight(0xffd0ad, 3.1)
        this.sunLight.position.set(-45, 70, 35)
        this.sunLight.castShadow = true
        this.sunLight.shadow.mapSize.set(2048, 2048)
        this.sunLight.shadow.camera.near = 1
        this.sunLight.shadow.camera.far = 220
        this.sunLight.shadow.camera.left = -115
        this.sunLight.shadow.camera.right = 115
        this.sunLight.shadow.camera.top = 115
        this.sunLight.shadow.camera.bottom = -115
        this.sunLight.shadow.bias = -0.00012
        this.sunLight.shadow.normalBias = 0.025
        this.scene.add(this.sunLight)
        this.scene.add(new THREE.HemisphereLight(0x9db9df, 0x353a2c, 0.85))
        const fill = new THREE.DirectionalLight(0x7899c2, 0.55)
        fill.position.set(55, 28, -60)
        this.scene.add(fill)
    }

    setSky() {
        const geometry = new THREE.SphereGeometry(600, 48, 32)
        const material = new THREE.ShaderMaterial({
            side: THREE.BackSide, depthWrite: false,
            vertexShader: `varying vec3 vDirection; void main(){ vec4 w=modelMatrix*vec4(position,1.0); vDirection=w.xyz-cameraPosition; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
            fragmentShader: `varying vec3 vDirection; void main(){ float h=normalize(vDirection).y; vec3 zenith=vec3(0.10,0.17,0.29); vec3 mid=vec3(0.30,0.42,0.57); vec3 horizon=vec3(0.82,0.55,0.39); vec3 c=mix(horizon,mid,smoothstep(-0.08,0.22,h)); c=mix(c,zenith,smoothstep(0.18,0.78,h)); float glow=exp(-pow((h-0.025)*13.0,2.0)); c=mix(c,vec3(0.98,0.68,0.45),glow*0.34); gl_FragColor=vec4(c,1.0); }`
        })
        this.sky = new THREE.Mesh(geometry, material)
        this.sky.frustumCulled = false
        this.scene.add(this.sky)
    }

    addSunGlow() {
        const glowMat = new THREE.SpriteMaterial({ map: this.createGlowTexture(), color: 0xffb77c, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0.32 })
        this.sunGlow = new THREE.Sprite(glowMat)
        this.sunGlow.position.set(-250, 90, -360)
        this.sunGlow.scale.set(150, 150, 1)
        this.scene.add(this.sunGlow)
    }

    createGlowTexture() {
        const size = 128, data = new Uint8Array(size * size * 4)
        for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
            const d = Math.sqrt(((x-size/2)/(size/2))**2 + ((y-size/2)/(size/2))**2), i = (y*size+x)*4
            data[i] = 255; data[i+1] = 190; data[i+2] = 130; data[i+3] = Math.max(0, Math.round(120*Math.pow(Math.max(0,1-d),2)))
        }
        const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
        tex.needsUpdate = true
        return tex
    }

    addDistantLandscape() {
        const group = new THREE.Group()
        const specs = [[-230,-260,105,48,0x3b4a4b],[-90,-310,150,68,0x465353],[100,-330,180,76,0x3b4a4b],[280,-250,125,52,0x4b5550],[-320,80,130,45,0x3d4b45],[320,100,150,55,0x414e47]]
        specs.forEach(([x,z,w,h,color]) => {
            const geo = new THREE.SphereGeometry(1, 24, 14, 0, Math.PI*2, 0, Math.PI/2)
            const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0 }))
            mesh.position.set(x, -3, z); mesh.scale.set(w, h, w*0.58); mesh.receiveShadow = true; group.add(mesh)
        })
        this.scene.add(group)
        this.landscape = group
    }

    addClouds() {
        this.cloudGroup = new THREE.Group()
        const positions = [[-190,105,-220,1.3],[170,120,-280,1.6],[270,88,80,1.1],[-260,90,150,1.25],[45,145,280,1.5]]
        const material = new THREE.MeshStandardMaterial({ color: this.colors.cloud, roughness: 1, metalness: 0, transparent: true, opacity: 0.78, depthWrite: false })
        positions.forEach(([x,y,z,s]) => {
            const cloud = new THREE.Group()
            ;[[0,0,0,13],[12,-2,0,10],[-12,-3,1,9],[4,6,-1,9],[-6,5,2,8]].forEach(([px,py,pz,r]) => {
                const puff = new THREE.Mesh(new THREE.SphereGeometry(r,16,12), material)
                puff.position.set(px,py,pz); puff.scale.z = 0.42; cloud.add(puff)
            })
            cloud.position.set(x,y,z); cloud.scale.setScalar(s); this.cloudGroup.add(cloud)
        })
        this.scene.add(this.cloudGroup)
    }
}
